/**
 * DashboardHeader Component
 * Simplified header for dashboard layout
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { BuyCreditsButton } from '@/components/credits/BuyCreditsButton';
import { cn } from '@/lib/utils';
import eclouLogo from '@/assets/logo_eclou_2025.png';

export interface DashboardHeaderProps {
  /** Additional class names */
  className?: string;
}

/**
 * Simplified dashboard header with user actions
 */
export function DashboardHeader({ className }: DashboardHeaderProps) {
  useTranslation();

  return (
    <header
      className={cn(
        'w-full',
        'px-4 py-3',
        'md:px-8 md:py-4',
        'lg:px-12',
        'bg-transparent',
        className
      )}
    >
      <div className="flex items-center gap-4">
        {/* Mobile brand logo — hidden on md+ where sidebar shows it */}
        <Link to="/dashboard" className="md:hidden">
          <img src={eclouLogo} alt="eclou" className="h-6" />
        </Link>

        <div className="ml-auto flex items-center gap-4">
        {/* Buy Credits */}
        <BuyCreditsButton />
        {/* Website */}
        <a
          href="https://www.eclou.net"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--color-charcoal)]/70 hover:text-[var(--color-charcoal)] transition-colors"
          aria-label="Website"
        >
          <Globe className="w-5 h-5" aria-hidden="true" />
        </a>
        {/* LinkedIn */}
        <a
          href="https://www.linkedin.com/company/eclou/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--color-charcoal)]/70 hover:text-[var(--color-charcoal)] transition-colors"
          aria-label="LinkedIn"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        </a>
        {/* Language Switcher */}
        <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}
